import { TrackPayload } from "@repo/edtech-analytics";

export enum VideoClickAction {
  PlayFromPreview = "playFromPreview",
  TogglePlay = "togglePlay",
  OnlyShowControls = "onlyShowControls",
}

export enum KeyPressAction {
  SkipPrevious = "skipPrevious",
  SkipNext = "skipNext",
  TogglePlay = "togglePlay",
  None = "none",
}

export enum SkipIndicator {
  Previous = "prev",
  Next = "next",
}

export type PlayerSpeed = "0,25" | "0,5" | "0,75" | "1" | "1,25" | "1,5" | "1,75" | "2";

export enum PlayerFileType {
  Video = "video/mp4",
  Audio = "audio/mp3",
}

export enum PlayerType {
  Video = "video",
  Audio = "audio",
}

export type Subscriber = (state: GlobalPlayerStoreState) => void;

export type PlayerState = {
  id: string,
  source: string,
  title: string,
  duration: number,
  currentTime: number,
  speed: PlayerSpeed,
  fileSize: number,
  isMobile: boolean,
  playing: boolean,
  ended: boolean,
  isLoading: boolean,
  pendingPlayByUser: boolean,
  initialLessonIndex: number,
  playerEl: HTMLMediaElement | null,
  analyticsTracker?: (params: TrackPayload, dedup?: boolean, shouldUpdate?: boolean) => void,
  clipMode?: boolean,
  muted: boolean,
  autoplay?: boolean,
};

export type PlayerActions = {
  play: () => void,
  pause: () => void,
  toggle: () => void,
  seekTo: (time: number) => void,
  reset: () => void,
  setSpeed: (speed: PlayerSpeed) => void,
  setMobile: (isMobile: boolean) => void,
  setActivate: (params: {
    id: string,
    source: string,
    title: string,
    clipMode?: boolean,
    autoplay?: boolean,
  }) => void,
  setFileSize: (size: number) => void,
  getPlayerElement: () => HTMLMediaElement | null,
  setPlayerEl: (el: HTMLMediaElement | null) => void,
  setAnalyticsTracker: (
    track?: (params: TrackPayload, dedup?: boolean, shouldUpdate?: boolean) => void
  ) => void,
  setMuted: (muted: boolean) => void,
};

export type DownloadState = {
  downloadProgress: number,
  downloadedSize: number,
  isDownloading: boolean,
  isPaused: boolean,
  isCompleted: boolean,
};

export type DownloadActions = {
  startDownload: (url?: string, fileSize?: number, fileType?: PlayerFileType) => void,
  pauseDownload: () => void,
  cancelDownload: () => void,
  completeDownload: () => void,
  updateProgress: (progress: number, size: number) => void,
};

export type MiniPlayerState = {
  isMini: boolean,
  onBackHandler?: () => void,
  onExitHandler?: () => void,
};

export type MiniActions = {
  toggleMini: () => void,
  enterMini: () => void,
  exitMini: () => void,
  handleMiniPlayer: () => Promise<void>,
  setMiniBackButtonHandler(fn?: () => void): () => void,
  setMiniExitButtonHandler(fn?: () => void): () => void,
  triggerMiniBackButton(onClickFromUI?: () => void): void,
};

export type ControlsState = {
  isShowingControls: boolean,
  showSettings: boolean,
  currentSkip: SkipIndicator | null,
  isFullscreen: boolean,
  isMobile: boolean,
  playerWrapEl: HTMLDivElement | null,
};

export type ControlsActions = {
  showControlsForLimitedTime: () => void,
  showControlsPermanently: () => void,
  clearControlsTimer: () => void,
  resetControls: () => void,
  toggleSettings: () => void,
  handleControlsPlayButtonClick: () => void,
  toggleFullscreen: () => void,
  setFullscreen: (v: boolean) => void,
  showSkip: (side: SkipIndicator) => void,
  onVideoClick: () => void,
  onVideoMobileTouch: (event: TouchEvent) => void,
  onVideoKeyDown: (e: KeyboardEvent) => void,
  bindVideoEvents: (playerWrapEl: HTMLMediaElement, onlyClick?: boolean) => () => void,
  setPlayerWrapEl: (el: HTMLDivElement | null) => void,
};

export type GlobalPlayerStoreState = & PlayerState & DownloadState & MiniPlayerState & ControlsState;

export type GlobalPlayerStore = {
  get: () => GlobalPlayerStoreState,
  set: (patch: Partial<GlobalPlayerStoreState>) => void,
  subscribe: (cb: Subscriber) => () => void,
  playerActions: PlayerActions,
  miniActions: MiniActions,
  downloadActions: DownloadActions,
  controlsActions: ControlsActions,
};

export type Snapshot = {
  url: string | null,
  width: number,
  height: number,
};

